<template>
  <div>
    <Modal modalId="cancel-subscription-modal" size="modal-md" @hidden="closeManagePlanModal()">
      <ModalHeader
        :title="$t('xCancelPlan')"
        icon="far fa-circle-dollar colorOrange mr-1"
        :style="{ padding: '4px 12px' }"
      >
        <div class="cols-auto">
          <button
            type="button"
            class="btn btn-icon-button btn-icon-button--gray"
            @click="closeManagePlanModal()"
            data-test-hook="CloseModalBtn"
          >
            <i class="fas fa-times" />
          </button>
        </div>
      </ModalHeader>
      <ModalBody>
        <div class="row mb-3">
          <div class="col">
            <p data-test-hook="EndOfSubscriptionText">
              {{ $t('xCurrentPlanDateText') }}
              {{ $moment(endDate).format('MM/DD/YYYY') + '.' }}
              {{ $t('xCurrentPlanText') }}
            </p>
          </div>
        </div>
        <div v-if="doesAccountHaveGen() && !doesAccountHaveFuelMonitor()">
          <div class="row mb-3">
            <div class="col">
              <span class="text" data-test-hook="BasicFeaturesTextGenOnly">
                {{ $t('xGenToReceiveBasicFeaturesText') }}
              </span>
            </div>
          </div>
          <strong>{{ $t('xPremiumGenFeaturesHeader') }}</strong>
          <ul>
            <li>{{ $t('xGenPremiumFeaturesItem1') }}</li>
            <li>{{ $t('xGenPremiumFeaturesItem2') }}</li>
            <li>{{ $t('xGenPremiumFeaturesItem3') }}</li>
            <li>{{ $t('xGenPremiumFeaturesItem4') }}</li>
          </ul>
        </div>
        <div v-else-if="!doesAccountHaveGen() && doesAccountHaveFuelMonitor()">
          <div class="row mb-3">
            <div class="col">
              <span class="text" data-test-hook="BasicFeaturesTextMonitorOnly">
                {{ $t('xMonitorToReceiveBasicFeaturesText') }}
              </span>
            </div>
          </div>
          <strong>{{ $t('xPremiumFuelMonitorFeaturesHeader') }}</strong>
          <ul>
            <li>{{ $t('xMonitorPremiumFeaturesItem1') }}</li>
            <li>{{ $t('xMonitorPremiumFeaturesItem2') }}</li>
            <li>{{ $t('xMonitorPremiumFeaturesItem3') }}</li>
            <li>{{ $t('xMonitorPremiumFeaturesItem4') }}</li>
          </ul>
        </div>
        <div v-else>
          <div class="row mb-3">
            <div class="col">
              <span class="text" data-test-hook="BasicFeaturesTextGenAndMonitor">
                {{ $t('xGenAndMonitorBasicFeaturesText') }}
              </span>
            </div>
          </div>
          <strong>{{ $t('xPremiumGenFeaturesHeader') }}</strong>
          <ul>
            <li>{{ $t('xGenPremiumFeaturesItem1') }}</li>
            <li>{{ $t('xGenPremiumFeaturesItem2') }}</li>
            <li>{{ $t('xGenPremiumFeaturesItem3') }}</li>
            <li>{{ $t('xGenPremiumFeaturesItem4') }}</li>
          </ul>
          <strong>{{ $t('xPremiumFuelMonitorFeaturesHeader') }}</strong>
          <ul>
            <li>{{ $t('xMonitorPremiumFeaturesItem1') }}</li>
            <li>{{ $t('xMonitorPremiumFeaturesItem2') }}</li>
            <li>{{ $t('xMonitorPremiumFeaturesItem3') }}</li>
            <li>{{ $t('xMonitorPremiumFeaturesItem4') }}</li>
          </ul>
        </div>
        <div class="row" data-test-hook="AreYouSureYouWantToCancelText">
          <div class="col">
            {{ $t('xAreYouSureYouWantToCancelText') }}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <div class="modal-buttons">
          <button
            type="button"
            class="btn btn-outline-warning"
            @click="closeManagePlanModal()"
            :title="$t('xManageYourPlan')"
            data-test-hook="KeepPlanBtn"
          >
            {{ $t('xKeepPlanButtonText') }}
          </button>
          <button
            type="button"
            class="btn btn-warning ml-2"
            @click="cancelSubscription()"
            :title="$t('xManageYourPlan')"
            data-test-hook="CancelPlanBtn"
          >
            {{ $t('xCancelPlanButtonText') }}
          </button>
        </div>
      </ModalFooter>
    </Modal>

    <Modal modalId="cancel-subscription-spinner-modal" size="modal-lg">
      <ModalBody style="padding-top: 100px; padding-bottom: 100px;">
        <div class="d-flex justify-content-center align-items-center">
          <b-spinner style="width: 10rem; height: 10rem;" variant="warning"></b-spinner>
        </div>
      </ModalBody>
    </Modal>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import * as Constants from '@/constants'

export default {
  name: 'CancelSubscriptionModal',
  data() {
    return {
      accountData: null,
      subscriptionDetails: null
    }
  },
  props: [
    'endDate',
    'isCancelLegacy',
    'cancelLegacyApparatusId',
    'cancelLegacyApparatusType',
    'cancelSubscriptionApparatuses',
    'cancelSubscriptionProductId',
    'cancelSubscriptionStore'
  ],
  computed: {
    ...mapState({
      accountProfile: state => state.account.accountProfile
    })
  },
  methods: {
    closeManagePlanModal() {
      this.closeModal('cancel-subscription-modal')
    },
    cancelSubscription() {
      this.closeModal('cancel-subscription-modal')
      this.openModal('cancel-subscription-spinner-modal')
      if (this.isCancelLegacy) {
        this.cancelLegacySubscription(this.accountData.organizationId, this.cancelLegacyApparatusId)
      } else {
        this.cancelSubscriptionProduct(
          this.accountData.organizationId,
          this.cancelSubscriptionStore,
          this.cancelSubscriptionProductId
        )
      }
    },
    async cancelLegacySubscription(orgId, apparatusId) {
      await this.$store
        .dispatch('cancelApparatusSubscription', {
          organizationId: orgId,
          apparatusId: apparatusId
        })
        .then(() => {
          this.pollForSubscriptionCancelDataAndRefresh()
        })
        .catch(e => {
          this.closeModal('cancel-subscription-spinner-modal')
          this.$store.dispatch('displayToast', {
            title: this.$t('xError'),
            message: this.$t('xFailedToCancelDeviceSubscription'),
            variant: 'danger'
          })
        })
    },
    async cancelSubscriptionProduct(orgId, store, productId) {
      await this.$store
        .dispatch('cancelSubscriptionProduct', {
          organizationId: orgId,
          store: store,
          productId: productId
        })
        .then(() => {
          this.pollForSubscriptionCancelDataAndRefresh()
        })
        .catch(e => {
          this.closeModal('cancel-subscription-spinner-modal')
          this.$store.dispatch('displayToast', {
            title: this.$t('xError'),
            message: this.$t('xFailedToCancelSubscriptionProduct'),
            variant: 'danger'
          })
        })
    },
    doesAccountHaveGen() {
      return (
        (this.isCancelLegacy && this.cancelLegacyApparatusType === Constants.HsbApparatusType) ||
        (!this.isCancelLegacy &&
          this.cancelSubscriptionApparatuses?.find(apparatus => apparatus.type === Constants.HsbApparatusType))
      )
    },
    doesAccountHaveFuelMonitor() {
      return (
        (this.isCancelLegacy && this.cancelLegacyApparatusType === Constants.FuelTankApparatusType) ||
        (!this.isCancelLegacy &&
          this.cancelSubscriptionApparatuses?.find(apparatus => apparatus.type === Constants.FuelTankApparatusType))
      )
    },
    async pollForSubscriptionCancelDataAndRefresh() {
      // Check for apparatus data repeatedly or for 5 seconds, if it doesn't show up, display toast
      const operationStart = Date.now()
      const delay = ms => new Promise(res => setTimeout(res, ms))
      while (
        Date.now() - operationStart < 5000 &&
        this.subscriptionDetails?.subscriptions[0]?.status !== Constants.SubscriptionDetailPendingCancel
      ) {
        const response = await this.$store
          .dispatch('getSubscriptionDetails', this.accountProfile.organizationId)
          .catch(() => {})
        this.subscriptionDetails = response.data
        await delay(1000)
      }
      // Different toast shown depending on if data has been updated
      if (this.subscriptionDetails?.subscriptions[0]?.status !== Constants.SubscriptionDetailPendingCancel) {
        this.closeModal('cancel-subscription-spinner-modal')
        this.closeModal('cancel-subscription-modal')
        this.$store.dispatch('displayToast', {
          title: this.$t('xSuccess'),
          message: this.$t('xSuccessfullyCancelledSubscriptionProductPleaseRefresh'),
          variant: 'success'
        })
        this.$store.dispatch('setSubscriptionPendingCancel', true)
      } else {
        this.closeModal('cancel-subscription-spinner-modal')
        this.closeModal('cancel-subscription-modal')
        const toast = this.$store.dispatch('displayToast', {
          title: this.$t('xSuccess'),
          message: this.$t('xSuccessfullyCancelledSubscriptionProductPleaseRefresh'),
          variant: 'success'
        })
        setTimeout(() => {
          window.location.reload()
        }, 3000)
      }
    }
  },
  async mounted() {
    if (this.accountProfile) {
      this.accountData = _.cloneDeep(this.accountProfile)
      await this.$store
        .dispatch('getSubscriptionDetails', this.accountProfile.organizationId)
        .then(response => {
          this.subscriptionDetails = response.data
          const purchaseDate = this.subscriptionDetails?.subscriptions[0]?.purchasedDate
          const duration = this.subscriptionDetails?.subscriptions[0]?.product?.durationInMonths
          if (purchaseDate && duration) {
            this.endDate = this.addMonths(purchaseDate, duration)
          } else this.endDate == null
        })
        .catch(() => {})
    } else {
      this.$store.dispatch('getAccountProfile')
    }
  }
}
</script>
